import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import { getConfigInfo } from 'utils/loanManagement';

import { closeDialogAction } from 'store/global/actions';

import { DOCUMENT_RECORD_FORM_CONFIG } from './DocumentRecordModal.config';
import { buttonsWrapperStyles, formWrapperStyles } from './DocumentRecordModal.styles';
import { DocumentRecordModalTestIds } from './DocumentRecordModal.testIds';

const DocumentRecordModal = ({ dialogId, documentRecord, onSave }) => {
  const dispatch = useDispatch();

  const applicationData = useMemo(
    () => ({
      documentType: documentRecord?.documentType ?? '',
      receivedDate: documentRecord?.receivedDate ?? null,
      toInvestorDate: documentRecord?.toInvestorDate ?? null,
      documentRecordingAmount: documentRecord?.documentRecordingAmount ?? null,
      finalCdRecordingAmount: documentRecord?.finalCdRecordingAmount ?? null,
      comments: documentRecord?.comments ?? '',
    }),
    [documentRecord],
  );

  const { initialFormValues, fieldsConfig, observableFieldsNames, schema } = useMemo(
    () => getConfigInfo({ applicationData, config: DOCUMENT_RECORD_FORM_CONFIG }),
    [applicationData],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setInForm,
    validateField,
    validateForm,
    setSchema,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  useEffect(() => {
    setSchema(schema);
  }, [schema, setSchema]);

  const handleClose = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const handleSave = useCallback(async () => {
    const isValid = await validateForm({ noFormChangedValidate: true });

    if (!isValid) {
      return;
    }

    const documentTypeValue = form.documentType?.value ?? form.documentType;

    const recordData = {
      documentType: documentTypeValue,
      receivedDate: form.receivedDate,
      toInvestorDate: form.toInvestorDate ?? null,
      documentRecordingAmount: form.documentRecordingAmount ?? null,
      finalCdRecordingAmount: form.finalCdRecordingAmount ?? null,
      comments: form.comments ?? '',
    };

    onSave(recordData, documentRecord?.key);
    handleClose();
  }, [form, onSave, documentRecord?.key, handleClose, validateForm]);

  return (
    <Box sx={formWrapperStyles}>
      <FormGenerator
        config={DOCUMENT_RECORD_FORM_CONFIG}
        form={form}
        handleChange={handleChange}
        numberOfColumns={2}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
      <ButtonGroupWrapper sx={buttonsWrapperStyles}>
        <Button
          data-testid={DocumentRecordModalTestIds.CANCEL_BUTTON}
          size="large"
          variant="line"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={DocumentRecordModalTestIds.SAVE_BUTTON}
          size="large"
          onClick={handleSave}
        >
          Save
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

DocumentRecordModal.propTypes = {
  dialogId: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  documentRecord: PropTypes.shape({
    key: PropTypes.string,
    documentType: PropTypes.string,
    receivedDate: PropTypes.string,
    toInvestorDate: PropTypes.string,
    documentRecordingAmount: PropTypes.number,
    finalCdRecordingAmount: PropTypes.number,
    comments: PropTypes.string,
  }),
};

export default memo(DocumentRecordModal);
